import { CookieStorage } from 'cookie-storage'
import { SimpleMemoryStorage } from 'simple-memory-storage'
import { UserTraits } from '@noticeable-app/noticeable-common'

export class WebStorage {
    private static readonly COOKIE_STORAGE = new CookieStorage()

    private static readonly COOKIE_OPTIONS = { path: '/', sameSite: 'None', secure: true } as const

    private static _hasStorage(name: string): boolean {
        try {
            const storage = window[name]
            const key = '__noticeable_storage_test'
            storage.setItem(key, '1')
            const value = storage.getItem(key)
            storage.removeItem(key)
            return value === '1'
        } catch (e) {
            return false
        }
    }

    public static getStorage(): Storage {
        if (WebStorage._hasStorage('localStorage')) {
            return window.localStorage
        } else if (WebStorage._hasStorage('sessionStorage')) {
            return window.sessionStorage
        } else {
            return new SimpleMemoryStorage()
        }
    }

    public static deleteUserId(projectId: string): void {
        WebStorage._removeCookie('noticeable_uid_' + projectId)
    }

    public static deleteUserSsoToken(projectId: string): void {
        WebStorage._removeCookie('noticeable_usso_' + projectId)
    }

    /**
     * Removes a cookie only when it exists. An unconditional removal is a write to
     * document.cookie with a past expiry: browsers drop it, but consent scanners
     * record it as a session cookie being set. The removal also carries the same
     * attributes as the creation so it matches the cookie in a cross-site iframe.
     *
     * Cookies written before the path was pinned to '/' are scoped to the directory
     * of the page that created them. If one is still visible after the root-path
     * removal, a second removal with the default path clears it.
     */
    private static _removeCookie(name: string): void {
        if (WebStorage.COOKIE_STORAGE.getItem(name) === null) {
            return
        }

        WebStorage.COOKIE_STORAGE.removeItem(name, WebStorage.COOKIE_OPTIONS)

        if (WebStorage.COOKIE_STORAGE.getItem(name) !== null) {
            const { path, ...legacyOptions } = WebStorage.COOKIE_OPTIONS
            WebStorage.COOKIE_STORAGE.removeItem(name, legacyOptions)
        }
    }

    public static deleteAllUserTraits(): void {
        for (const key in sessionStorage) {
            const chunks = key.split('_')
            if (chunks.length === 3 && chunks[0] === 'noticeable' && chunks[1] === 'traits') {
                sessionStorage.removeItem(key)
            }
        }
    }

    public static deleteUserTraits(projectId: string): void {
        sessionStorage.removeItem('noticeable_traits_' + projectId)
    }

    public static getUserId(projectId: string): string | null {
        return WebStorage.COOKIE_STORAGE.getItem('noticeable_uid_' + projectId)
    }

    public static getUserSsoToken(projectId: string): string | null {
        return WebStorage.COOKIE_STORAGE.getItem('noticeable_usso_' + projectId)
    }

    public static getUserIds(): Map<string, string> {
        return this._getIdCookieValueByProject('noticeable_uid')
    }

    public static getUserSsoTokens(): Map<string, string> {
        return this._getIdCookieValueByProject('noticeable_usso')
    }

    public static getAllUserTraits(): Map<string, UserTraits> {
        const result = new Map()

        for (const key in sessionStorage) {
            const chunks = key.split('_')
            if (chunks.length === 3 && chunks[0] === 'noticeable' && chunks[1] === 'traits') {
                const value = sessionStorage.getItem(key)
                if (value) {
                    result.set(chunks[2], JSON.parse(value))
                }
            }
        }

        return result
    }

    public static getUserTraits(projectId: string): Partial<UserTraits> {
        const traits = sessionStorage.getItem('noticeable_traits_' + projectId)
        if (!traits) {
            return {}
        }

        return JSON.parse(traits)
    }

    public static _getIdCookieValueByProject(prefix: string): Map<string, string> {
        const cookies = WebStorage.COOKIE_STORAGE
        const result = new Map()

        for (const cookieName in cookies) {
            if (cookieName.startsWith(prefix)) {
                const cookieNameChunks = cookieName.split('_')
                if (cookieNameChunks.length === 3) {
                    result.set(cookieNameChunks[2], cookies[cookieName])
                }
            }
        }

        return result
    }

    public static reset(projectId?: string): void {
        if (projectId) {
            this.deleteUserId(projectId)
            this.deleteUserTraits(projectId)
        } else {
            for (const cookie in WebStorage.COOKIE_STORAGE) {
                if (cookie.startsWith('noticeable_uid_')) {
                    WebStorage._removeCookie(cookie)
                }
            }

            this.deleteAllUserTraits()
        }
    }

    public static setUserId(projectId: string, value: string): void {
        if (!value) {
            WebStorage.deleteUserId(projectId)
            return
        }

        WebStorage.COOKIE_STORAGE.setItem('noticeable_uid_' + projectId, value, {
            expires: new Date(Date.now() + 31536000000),
            ...WebStorage.COOKIE_OPTIONS,
        })
    }

    public static setUserSsoToken(projectId: string, value: string): void {
        if (!value) {
            WebStorage.deleteUserSsoToken(projectId)
            return
        }

        WebStorage.COOKIE_STORAGE.setItem('noticeable_usso_' + projectId, value, {
            expires: new Date(Date.now() + 31536000000),
            ...WebStorage.COOKIE_OPTIONS,
        })
    }

    public static setUserTraits(projectId, traits: UserTraits): void {
        if (!traits || Object.keys(traits).length === 0) {
            sessionStorage.removeItem('noticeable_traits_' + projectId)
            return
        }

        sessionStorage.setItem('noticeable_traits_' + projectId, JSON.stringify(traits))
    }
}
